import Block from '@/components/Block'
import Button from '@/components/Button'
import Card from '@/components/Card'
import Layout, { Stack } from '@/components/Layout'
import Text from '@/components/Text'
import { useAuth } from '@/context/auth'
import StatItem, { Props as StatItemProps } from '@/widgets/StatItem'
import { useTranslation } from 'next-i18next'
import React from 'react'
import { coreSectionPaddingClassesTopHalf } from '../../constants/measured-scope'
import * as styles from './style'

export interface Props {
  title?: string
  subtitle?: string
  companyInfoItems: Array<{
    companyStats: StatItemProps[]
    featuredLogos: Array<{ imageUrl: string; title: string }>
  }>
  className?: string
}
const AboutBlock: React.FC<Props> = ({ title, subtitle, companyInfoItems: items, className = '' }) => {
  const { t } = useTranslation('common')
  const {
    state: { user },
  } = useAuth()

  if (user) return null

  return (
    <Block theme="white" className={`${className} pt-0 px-sm-lte-smallTablet`}>
      <div className={styles.root}>
        {title && (
          <Text
            size="lg"
            tag="h2"
            weight="lg"
            className={`${styles.blockTitle} ${coreSectionPaddingClassesTopHalf} ta-c`}
          >
            {title}
          </Text>
        )}
        {subtitle && (
          <Text weight="sm" tag="p" size="xs" className={'ta-c mb-lg'}>
            {subtitle}
          </Text>
        )}
        {!!items?.length && (
          <Layout spacing="lg">
            <Stack y>
              {items.map(({ companyStats, featuredLogos }, infoIdx) => (
                <React.Fragment key={`companyInfo_${infoIdx}`}>
                  <Card
                    theme="primaryMuted"
                    className={`height-unset-gte-desktop ${styles.statsWrapper} stats`}
                    size="xs"
                    overlay={false}
                  >
                    <Layout>
                      <Stack className={styles.statsContainer} y>
                        {companyStats.map(({ icon = 'people', title: statTitle, subtitle }, statIdx) => (
                          <StatItem
                            key={`stat_${infoIdx}:${statIdx}`}
                            icon={icon}
                            title={statTitle}
                            subtitle={subtitle}
                          />
                        ))}
                      </Stack>
                    </Layout>
                  </Card>
                  <Card
                    className={`height-unset-gte-desktop ${styles.statsWrapper}`}
                    overlay={false}
                    size="sm"
                    theme="tertiary"
                  >
                    <Text className={`${styles.title} ta-c`} weight="md" size="xs">
                      {t('featuredIn')}
                    </Text>
                    <Layout spacing="lg">
                      <Stack className={styles.pressContainer} y>
                        {featuredLogos.map(({ imageUrl, title: logoTitle }, logoIdx) => (
                          <img
                            key={`logo_${infoIdx}-${logoIdx}`}
                            src={imageUrl}
                            alt={logoTitle}
                            style={{ maxWidth: '80%', maxHeight: 30 }}
                            loading="lazy"
                          />
                        ))}
                      </Stack>
                    </Layout>
                  </Card>
                </React.Fragment>
              ))}
            </Stack>
          </Layout>
        )}
        <Layout className="pt-lg p-lg-gte-desktop">
          <Stack y alignItems="center">
            <div>
              <Button
                theme="white"
                component="link"
                href="https://blog.getsetup.io/press-page"
                iconRight="chevron-right"
                label={t('learnMore')}
              />
            </div>
          </Stack>
        </Layout>
      </div>
    </Block>
  )
}
export default AboutBlock
